import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Mail, MoreHorizontal, Plus, Search, Copy, Trash2, Pencil, Ticket, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useApi, useMe, errMsg } from "@/lib/api";
import { useResumeImport } from "@/components/ResumeImport";

const path = (d) => (d.type === "resume" ? `/resume/${d.id}` : `/cover-letter/${d.id}`);
const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const DocCard = ({ d, i, onOpen, onDuplicate, onDelete }) => {
  const Icon = d.type === "resume" ? FileText : Mail;
  return (
    <div className="rise group relative flex flex-col rounded-xl border border-paper-border bg-white p-5 transition-shadow hover:shadow-lg" style={{ animationDelay: `${i * 50}ms` }} data-testid={`doc-card-${d.id}`}>
      <button className="absolute inset-0 rounded-xl" onClick={onOpen} aria-label={`Open ${d.title}`} data-testid={`doc-open-${d.id}`} />
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-paper-subtle text-ink transition-colors group-hover:bg-thread-light group-hover:text-thread"><Icon className="h-5 w-5" /></span>
        <DropdownMenu>
          <DropdownMenuTrigger className="relative z-10 rounded-md p-1.5 text-ink-soft hover:bg-paper-subtle" data-testid={`doc-menu-${d.id}`}><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen} data-testid={`doc-edit-${d.id}`}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate} data-testid={`doc-duplicate-${d.id}`}><Copy className="mr-2 h-4 w-4" />Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`doc-delete-${d.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="mt-5 line-clamp-2 font-serif text-xl text-ink">{d.title}</p>
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-5 font-mono text-[10.5px] uppercase tracking-wider text-taupe">
        <span>{d.type === "resume" ? "Resume" : "Cover letter"}</span><span>·</span><span>{d.template}</span><span>·</span><span>{fmt(d.updated_at)}</span>
        <span className={`ml-auto rounded px-1.5 py-0.5 ${d.can_download ? "bg-green-50 text-green-700" : "bg-paper-subtle"}`}>{d.can_download ? "Unlocked" : "Preview"}</span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const imp = useResumeImport(api, (d) => navigate(path(d)));

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [api]);

  const create = async (type, example = false) => {
    try {
      const { data } = await api.post("/documents", { type, example });
      navigate(path(data));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const duplicate = async (d) => { await api.post(`/documents/${d.id}/duplicate`); toast.success("Duplicated document"); load(); };
  const remove = async () => { await api.delete(`/documents/${toDelete.id}`); toast.success("Deleted"); setToDelete(null); load(); };

  const shown = (docs || []).filter((d) => d.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-paper">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-14">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="rise">
            <p className="eyebrow">Your studio</p>
            <h1 className="mt-2 text-4xl text-ink sm:text-5xl">My documents</h1>
            {me?.pass_active && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 text-sm text-green-800" data-testid="pass-active-banner">
                <Ticket className="h-4 w-4" /> 30-Day Pass active until {fmt(me.pass_expires_at)}
              </p>
            )}
          </div>
          <div className="rise rise-1 flex flex-wrap gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="btn-thread" data-testid="new-resume-btn"><Plus className="h-4 w-4" /> New resume</DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => create("resume")} data-testid="new-resume-blank"><FileText className="mr-2 h-4 w-4" />Start blank</DropdownMenuItem>
                <DropdownMenuItem onClick={() => create("resume", true)} data-testid="new-resume-example"><Sparkles className="mr-2 h-4 w-4" />Start from an example</DropdownMenuItem>
                <DropdownMenuItem onClick={imp.open} data-testid="new-resume-import"><imp.Icon className="mr-2 h-4 w-4" />Import PDF or Word file</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button className="btn-ink" onClick={() => create("cover_letter")} data-testid="new-cover-letter-btn"><Mail className="h-4 w-4" /> New cover letter</button>
          </div>
        </div>

        <div className="relative mt-10 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <Input className="field pl-9" placeholder="Search documents" value={q} onChange={(e) => setQ(e.target.value)} data-testid="doc-search-input" />
        </div>

        {docs && docs.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-paper-border bg-white/60 p-12 text-center" data-testid="empty-state">
            <h2 className="text-2xl text-ink">A blank page, waiting for you</h2>
            <p className="mx-auto mt-2 max-w-md text-ink-soft">Start with an example to see how Pitchpaper works, or begin from scratch.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3"><button className="btn-thread" onClick={() => create("resume", true)} data-testid="empty-start-example-btn"><Sparkles className="h-4 w-4" /> Try the example resume</button>
            <button className="btn-ink" onClick={imp.open} data-testid="empty-import-btn"><imp.Icon className="h-4 w-4" /> Import my resume</button></div>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="doc-grid">
            {shown.map((d, i) => (
              <DocCard key={d.id} d={d} i={i} onOpen={() => navigate(path(d))} onDuplicate={() => duplicate(d)} onDelete={() => setToDelete(d)} />
            ))}
          </div>
        )}
      </main>

      {imp.picker}
      {imp.overlay}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{toDelete?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone. Any single-download unlock on this document is lost too.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
